import { EntryTypeEnum } from '@insurance/api/requests'
import { InputDatePicker } from '@insurance/components'
import { useValidationsField } from '@insurance/hooks'
import { InputParsedNumber } from '@shared/components'
import { Col, Form, Row } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'

import { MultipleDaysSelect } from './MultipleDaysSelect'
import {
  INSURANCE_DURATION_MAX_DAYS,
  INSURANCE_DURATION_MIN_DAYS
} from './utils'
import { ValidityMonths } from './ValidityMonths'

export const InsuranceDates = () => {
  const { t } = useTranslation()
  const formInstance = Form.useFormInstance()
  const tripType = Form.useWatch('entry_type')
  const validFrom = Form.useWatch('valid_from')
  const insuranceAfterDays =
    Form.useWatch('insurance_after_count_days', { preserve: true }) || 0
  const { required } = useValidationsField()

  const isMultiple = tripType === EntryTypeEnum.MULTIPLE

  const handleChangeStartInsurance = (date: Dayjs) => {
    const endInsuranceDate = formInstance.getFieldValue('valid_to')
    const insuredDays = dayjs(endInsuranceDate).diff(date, 'd') + 1

    const isNotValidEndDays =
      dayjs(endInsuranceDate).diff(date, 'd') < INSURANCE_DURATION_MIN_DAYS - 1

    if (isNotValidEndDays) {
      formInstance.setFieldValue(
        'valid_to',
        dayjs(date).add(INSURANCE_DURATION_MIN_DAYS - 1, 'd')
      )
      formInstance.setFieldValue('count_days', INSURANCE_DURATION_MIN_DAYS)
      return
    }

    formInstance.setFieldValue('count_days', insuredDays)
  }

  const handleChangeEndInsurance = (date: Dayjs) => {
    const startInsuranceDate = formInstance.getFieldValue('valid_from')
    const insuredDays = dayjs(date).diff(startInsuranceDate, 'd') + 1
    formInstance.setFieldValue('count_days', insuredDays)
  }

  const handleChangeCountDays = (days: string | number | null = 0) => {
    if (!days) return

    const startInsuranceDate = formInstance.getFieldValue('valid_from')
    const newEndInsurance = dayjs(startInsuranceDate).add(Number(days) - 1, 'd')

    formInstance.setFieldValue('valid_to', newEndInsurance)
  }

  return (
    <Row gutter={[12, 12]}>
      <Col xs={{ span: 24 }} md={{ span: 8 }}>
        <Form.Item
          name="valid_from"
          label={t('beginning_insurance_policy')}
          rules={[required]}
        >
          <InputDatePicker
            minDate={dayjs().add(insuranceAfterDays, 'd')}
            onChange={handleChangeStartInsurance}
            allowClear={false}
          />
        </Form.Item>
      </Col>

      {!isMultiple ? (
        <>
          <Col xs={{ span: 24 }} md={{ span: 8 }}>
            <Form.Item
              name="valid_to"
              label={t('ending_insurance_policy')}
              rules={[required]}
            >
              <InputDatePicker
                minDate={dayjs(validFrom).add(
                  INSURANCE_DURATION_MIN_DAYS - 1,
                  'd'
                )}
                onChange={handleChangeEndInsurance}
                allowClear={false}
              />
            </Form.Item>
          </Col>

          <Col xs={{ span: 24 }} md={{ span: 8 }}>
            <Form.Item
              name="count_days"
              label={t('insured_days')}
              normalize={(value) =>
                value ? value : INSURANCE_DURATION_MIN_DAYS
              }
              rules={[required]}
            >
              <InputParsedNumber
                min={INSURANCE_DURATION_MIN_DAYS}
                max={INSURANCE_DURATION_MAX_DAYS}
                type="number"
                placeholder={t('insured_days')}
                onChange={handleChangeCountDays}
              />
            </Form.Item>
          </Col>
        </>
      ) : (
        <>
          <Col xs={{ span: 24 }} md={{ span: 8 }}>
            <Form.Item name="days" label={t('insured_days')} rules={[required]}>
              <MultipleDaysSelect placeholder={t('insured_days')} />
            </Form.Item>
          </Col>

          <Col xs={{ span: 24 }} md={{ span: 8 }}>
            <Form.Item
              name="term"
              label={t('insurance_validity')}
              rules={[required]}
            >
              <ValidityMonths placeholder={t('insurance_validity')} />
            </Form.Item>
          </Col>
        </>
      )}
    </Row>
  )
}
